import * as React from 'react';
import { useMemo, useState } from 'react';
import Dots from 'assets/icons/dots.svg?react';
import {
  OperationType,
  ProjectListItemDto,
  ProjectResourceType,
} from 'services/api/api-client';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { DeleteProjectModal } from '../deleteProjectModal/DeleteProjectModal';
import { useNavigate } from 'react-router';
import { Links } from 'application/constants/links';
import { Emoji } from 'emoji-picker-react';
import styles from './ProjectCard.module.scss';
import { AppMenu } from 'components/uikit/menu/AppMenu';
import { ProjectModal } from '../projectModal/ProjectModal.tsx';
import { PermissionGate } from 'helpers/projectPermissions/PermissionGate.tsx';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';
import { ProjectProvider } from 'helpers/projectPermissions/ProjectContext.tsx';

export type ProjectCardProps = {
  project: ProjectListItemDto;
  className?: string;
};

export const ProjectCard: React.FC<ProjectCardProps> = (props) => {
  const i18n = useScopedTranslation('Component.ProjectCard');
  const navigate = useNavigate();

  const menuAnchorRef = React.useRef<HTMLElement>(null);
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const onShowContextualMenu = React.useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      ev.stopPropagation();
      ev.preventDefault();
      setShowContextualMenu(true);
    },
    [],
  );

  const menuItems = useMemo(() => {
    return [
      {
        key: 'edit',
        text: i18n.t('edit_button'),
        className: styles.edit,
        onClick: () => {
          setShowContextualMenu(false);
          setIsModalOpen(true);
        },
      },
      {
        key: 'delete',
        text: i18n.t('delete_button'),
        className: styles.delete,
        onClick: () => {
          setShowContextualMenu(false);
          setIsDeleteModalOpen(true);
        },
      },
    ];
  }, [i18n]);

  return (
    <ProjectProvider data={props.project}>
      <div
        className={styles.card}
        onClick={() => {
          navigate(
            Links.Authorized.ProjectDetails.link({
              projectId: props.project.id,
            }),
          );
        }}
        data-test-id="project-card"
      >
        <div data-test-id="project-icon">
          <Emoji unified={props.project.iconCode} size={48} />
        </div>

        <div className={styles.project_description}>
          <div className={styles.project_name} data-test-id="project-name">
            {props.project.name}
          </div>
          <div className={styles.project_revision}>
            <span>{i18n.t('current_revision')}: </span>
            <span data-test-id="project-version">
              {props.project.currentRevision}
            </span>
          </div>
        </div>
        <PermissionGate
          permissions={[
            permission(
              ProjectResourceType.Project,
              OperationType.Update,
              OperationType.Read,
              OperationType.Delete,
            ),
          ]}
          allowForAdmin
          ignoreFeatureFlag
        >
          <div>
            <div
              ref={menuAnchorRef as any}
              className={styles.dotMenu}
              onClick={onShowContextualMenu}
            >
              <Dots className={styles.dotMenuImage} />
            </div>
            <AppMenu
              anchorEl={menuAnchorRef.current}
              open={showContextualMenu}
              onClose={() => {
                setShowContextualMenu(false);
              }}
              menuItems={menuItems}
              anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            />
          </div>
        </PermissionGate>

        <ProjectModal
          onClose={() => setIsModalOpen(false)}
          id={props.project.id}
          isOpen={isModalOpen}
        />

        <DeleteProjectModal
          onClose={() => setIsDeleteModalOpen(false)}
          projectId={props.project.id}
          projectName={props.project.name}
          isOpen={isDeleteModalOpen}
        />
      </div>
    </ProjectProvider>
  );
};
